/**
 * File: retainers.ts
 * Path: packages/config/src/retainers/retainers.ts
 * Description: Retainers and care plans: who pays a monthly fee, from when,
 * and whether it is still going.
 *
 * A signed retainer (or venture) contract is a retainer automatically. Staff
 * can add a care plan that has no contract by hand, and can pause or cancel
 * either kind, or mark it completed (it ran its term and is paid up, so it
 * stops without counting as a lost client): that is stored in retainers/{id} (for a contract's retainer
 * the id is the contract's id, so it overrides what the contract says).
 * The same document holds the plan's "send automatically" switch.
 * Fees are whole cents.
 */

import { randToCents } from "../accounting/money";
import { contractSignedOn, southAfricanDay } from "../contracts/contractDue";
import type { Contract } from "../contracts/contractTypes";

export const RETAINER_STATUSES = ["active", "paused", "completed", "cancelled"] as const;
export type RetainerStatus = (typeof RETAINER_STATUSES)[number];

export const RETAINER_STATUS_LABELS: Record<RetainerStatus, string> = {
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * Whether a plan has stopped for good: completed (ran its term, paid up)
 * or cancelled (the client stopped it early). Only cancelled counts as a
 * lost client.
 */
export const planEnded = (status: RetainerStatus): boolean => status === "completed" || status === "cancelled";

export interface Retainer {
  /** The contract's id for a contract's retainer, else a made-up id. */
  id: string;
  clientId: string | null;
  clientName: string;
  /** Monthly fee in whole cents. */
  monthlyCents: number;
  /** YYYY-MM-DD. */
  startDate: string;
  status: RetainerStatus;
  /** YYYY-MM-DD, when it stopped or will stop. */
  endDate: string | null;
  contractId: string | null;
  /** "Care plan", "Retainer" or "Venture retainer". */
  kind: string;
  /** YYYY-MM-DD, when the plan runs out and needs renewing; null until set (then a year after it started). */
  renewsOn: string | null;
  /**
   * Each month's invoice goes out by itself, with no tap (planBilling.ts),
   * unless something holds it for Liam. Off unless staff turn it on;
   * absent reads as off.
   */
  autoSend?: boolean;
}

type Raw = Record<string, unknown>;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** A stored retainer; null when it is not one. */
export const toRetainer = (id: string, data: Raw | null | undefined): Retainer | null => {
  const r = data ?? {};
  const status = RETAINER_STATUSES.find((s) => s === r.status);
  const cents = typeof r.monthlyCents === "number" ? r.monthlyCents : NaN;
  if (!status || !Number.isInteger(cents) || cents < 0 || !DATE.test(str(r.startDate))) return null;
  return {
    id,
    clientId: str(r.clientId) || null,
    clientName: str(r.clientName),
    monthlyCents: cents,
    startDate: str(r.startDate),
    status,
    endDate: DATE.test(str(r.endDate)) ? str(r.endDate) : null,
    contractId: str(r.contractId) || null,
    kind: str(r.kind) || "Care plan",
    renewsOn: DATE.test(str(r.renewsOn)) ? str(r.renewsOn) : null,
    autoSend: r.autoSend === true,
  };
};

const addMonths = (day: string, months: number): string => {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
};

/** The day the plan runs out: the saved date, else a year after it started. */
export const renewalDate = (r: Pick<Retainer, "startDate" | "renewsOn">): string => r.renewsOn ?? addMonths(r.startDate, 12);

/** Whole days from `today` to a YYYY-MM-DD day; negative when it has passed. */
export const daysToDay = (day: string, today: string): number =>
  Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);

const KIND: Record<string, string> = { retainer: "Retainer", venture: "Venture retainer" };

/** The retainers signed contracts make, with any pause or cancellation staff saved over them. */
export const mergeRetainers = (contracts: readonly Contract[], stored: readonly Retainer[]): Retainer[] => {
  const saved = new Map(stored.map((r) => [r.id, r]));
  const fromContracts = contracts.flatMap((c): Retainer[] => {
    const template = c.content.template;
    if (template !== "retainer" && template !== "venture") return [];
    if (c.status !== "client_signed" && c.status !== "signed") return [];
    const override = saved.get(c.id);
    const base: Retainer = {
      id: c.id,
      clientId: c.clientId,
      clientName: c.content.client.name,
      monthlyCents: randToCents(c.content.monthlyFee),
      startDate: /^\d{4}-\d{2}-\d{2}$/.test(c.content.commencementDate)
        ? c.content.commencementDate
        : (contractSignedOn(c) ?? ""),
      status: "active",
      endDate: null,
      contractId: c.id,
      kind: KIND[template] ?? "Retainer",
      renewsOn: null,
      autoSend: false,
    };
    if (!base.startDate) return [];
    return [
      override
        ? { ...base, status: override.status, endDate: override.endDate, renewsOn: override.renewsOn, autoSend: override.autoSend === true }
        : base,
    ];
  });
  const contractIds = new Set(fromContracts.map((r) => r.id));
  const manual = stored.filter((r) => r.contractId === null && !contractIds.has(r.id));
  return [...fromContracts, ...manual].sort((a, b) => a.clientName.localeCompare(b.clientName));
};

export interface RetainerStats {
  /** Monthly recurring income from active retainers, whole cents. */
  mrrCents: number;
  activeCount: number;
  /** Cancelled with an end date in the last 90 days. */
  cancelledRecently: number;
  /** Cancelled recently as a share of (active + cancelled recently), whole percent. */
  churnPercent: number;
  /** Clients with a signed website (service) contract. */
  websiteClients: number;
  /** Of those, how many also have a retainer or care plan that is still going (not completed or cancelled). */
  withCarePlan: number;
}

const DAY_MS = 86_400_000;

export const retainerStats = (
  retainers: readonly Retainer[],
  contracts: readonly Contract[],
  now: number,
): RetainerStats => {
  const active = retainers.filter((r) => r.status === "active");
  const since = southAfricanDay(now - 90 * DAY_MS);
  const cancelledRecently = retainers.filter(
    (r) => r.status === "cancelled" && r.endDate !== null && r.endDate >= since,
  ).length;
  const base = active.length + cancelledRecently;
  const websites = new Set(
    contracts
      .filter((c) => c.content.template === "service" && c.clientId && (c.status === "client_signed" || c.status === "signed"))
      .map((c) => c.clientId as string),
  );
  const planned = new Set(retainers.filter((r) => !planEnded(r.status) && r.clientId).map((r) => r.clientId as string));
  return {
    mrrCents: active.reduce((s, r) => s + r.monthlyCents, 0),
    activeCount: active.length,
    cancelledRecently,
    churnPercent: base === 0 ? 0 : Math.round((cancelledRecently / base) * 100),
    websiteClients: websites.size,
    withCarePlan: [...websites].filter((id) => planned.has(id)).length,
  };
};
